import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, effect, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { AlbumDetails, BrowseOptions, Genre, MusicDataService } from '../music-data.service';
import { FavouriteAlbumsService } from '../favourite-albums.service';
import { FavouriteArtistsService } from '../favourite-artists.service';
import { ArtistGridComponent } from '../shared/artist-grid.component';
import { AlbumGridComponent } from '../shared/album-grid.component';
import { AlbumSortControlsComponent } from '../shared/album-sort-controls.component';
import { PagerComponent } from '../shared/pager.component';
import { loadSavedState, saveState } from '../shared/saved-state';
import { AlbumSort, MAX_PAGES, PAGE_SIZE, filterAlbumsByYear, pageCount, pageSlice, releaseYears, sortAlbums, yearOptions } from '../shared/album-list';

export type BrowseMode = 'albums' | 'artists';

const STATE_KEY = 'topAlbums.filters';
const SORTS: AlbumSort[] = ['top', 'newest', 'oldest'];
const DEFAULT_STATE = { mode: 'albums', genre: 0, subgenre: '', language: '', mood: '', decade: '', sort: 'top', year: 0, page: 1 };

// Sources hold at most this many items, so a list never spans more than MAX_PAGES pages
const MAX_ITEMS = PAGE_SIZE * MAX_PAGES;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-new-releases',
  templateUrl: './new-releases.component.html',
  styleUrls: ['./new-releases.component.css'],
  imports: [AlbumGridComponent, ArtistGridComponent, AlbumSortControlsComponent, PagerComponent]
})
export class NewReleasesComponent implements OnInit, OnDestroy {
  readonly years = yearOptions();

  // Last-used filters, restored from the previous visit
  private saved = loadSavedState(STATE_KEY, DEFAULT_STATE);

  mode = signal<BrowseMode>(this.saved.mode === 'artists' ? 'artists' : 'albums');
  allGenres = signal<Genre[]>([]);
  options = signal<BrowseOptions>({ languages: [], moods: [], decades: [], subgenres: {} });

  selectedGenre = signal(this.saved.genre);
  subgenre = signal(this.saved.subgenre);   // curated sub-genre key within the selected genre, '' = whole genre chart
  language = signal(this.saved.language);   // curated language key, '' = no language filter
  mood = signal(this.saved.mood);           // curated mood playlist key (chill, workout, ...)
  decade = signal(this.saved.decade);       // curated decade playlist key (90s, 00s, ...)
  sort = signal<AlbumSort>(SORTS.includes(this.saved.sort as AlbumSort) ? this.saved.sort as AlbumSort : 'top');
  year = signal(this.years.includes(this.saved.year) ? this.saved.year : 0);        // 0 = any year
  page = signal(Math.max(1, Math.floor(this.saved.page) || 1));

  items = signal<any[]>([]);          // albums or artists from the current source
  loading = signal(true);
  datesLoading = signal(false);
  error = signal<string | null>(null);

  // Language, mood and decade lists are curated playlists and can't be combined with each other or a genre
  curatedKey = computed(() => this.language() || this.mood() || this.decade() || this.subgenre());
  subgenreOptions = computed(() => this.options().subgenres[String(this.selectedGenre())] ?? []);
  // Genres with no albums are not offered (the selected one stays so the user can see where they are)
  genres = computed(() => this.allGenres().filter(g => g.albumCount === null || g.albumCount > 0 || g.id === this.selectedGenre()));

  // Release years that have albums in the current list; null until the dates have loaded
  datesLoaded = signal(false);
  availableYears = computed(() => this.datesLoaded() ? releaseYears(this.items()) : null);

  // Albums after the year filter and sort; artists are shown in chart order
  filtered = computed(() => {
    if (this.mode() === 'artists') return this.items();
    return sortAlbums(filterAlbumsByYear(this.items(), this.year()), this.sort());
  });
  pages = computed(() => pageCount(this.filtered().length));
  visible = computed(() => pageSlice(this.filtered(), this.page()));

  private subs = new Subscription();
  private sourceSub?: Subscription;
  private datesSub?: Subscription;
  private restorePage: number | null = this.page();

  constructor(private musicdata: MusicDataService, protected favourites: FavouriteAlbumsService, protected artistFavourites: FavouriteArtistsService) {
    effect(() => {
      saveState(STATE_KEY, {
        mode: this.mode(), genre: this.selectedGenre(), subgenre: this.subgenre(),
        language: this.language(), mood: this.mood(), decade: this.decade(),
        sort: this.sort(), year: this.year(), page: this.page()
      });
    });
  }

  ngOnInit() {
    this.favourites.ensureIds();
    this.artistFavourites.ensureIds();
    this.subs.add(this.musicdata.getGenres().subscribe({
      next: (genres) => this.allGenres.set(genres),
      error: (err) => console.error('Error fetching genres:', err)
    }));
    this.subs.add(this.musicdata.getBrowseOptions().subscribe({
      next: (options) => this.options.set(options),
      error: (err) => console.error('Error fetching browse options:', err)
    }));
    this.loadSource();
  }

  toggleFavouriteArtist(artist: { id: string; name: string }) {
    this.artistFavourites.toggle(artist.id, artist.name);
  }

  toggleFavourite(album: { id: string; name: string }) {
    this.favourites.toggle(album.id, album.name);
  }

  setMode(mode: BrowseMode) {
    if (mode === this.mode()) return;
    this.mode.set(mode);
    this.clearCurated();
    this.loadSource();
  }

  selectGenre(id: number) {
    if (id === this.selectedGenre() && !this.curatedKey()) return;
    this.selectedGenre.set(id);
    this.clearCurated();
    this.loadSource();
  }

  selectSubgenre(key: string) {
    const next = key === this.subgenre() ? '' : key;
    this.clearCurated();
    this.subgenre.set(next);
    this.loadSource();
  }

  selectLanguage(key: string) {
    if (key === this.language()) return;
    this.clearCurated();
    this.language.set(key);
    if (key) this.selectedGenre.set(0);
    this.loadSource();
  }

  selectMood(key: string) {
    if (key === this.mood()) return;
    this.clearCurated();
    this.mood.set(key);
    if (key) this.selectedGenre.set(0);
    this.loadSource();
  }

  selectDecade(key: string) {
    if (key === this.decade()) return;
    this.clearCurated();
    this.decade.set(key);
    if (key) this.selectedGenre.set(0);
    this.loadSource();
  }

  private clearCurated() {
    this.language.set('');
    this.mood.set('');
    this.decade.set('');
    this.subgenre.set('');
  }

  setSort(sort: AlbumSort) {
    if (sort === this.sort()) return;
    this.sort.set(sort);
    this.page.set(1);
  }

  setYear(year: number) {
    if (year === this.year()) return;
    this.year.set(year);
    this.page.set(1);
  }

  setPage(page: number) {
    this.page.set(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private loadSource() {
    if (this.sourceSub && !this.sourceSub.closed) this.restorePage = null;
    this.sourceSub?.unsubscribe();
    this.datesSub?.unsubscribe();
    this.loading.set(true);
    this.datesLoading.set(false);
    this.datesLoaded.set(false);
    this.error.set(null);
    this.page.set(1);

    const key = this.curatedKey();
    const request = this.mode() === 'artists'
      ? this.musicdata.getTopArtists(this.selectedGenre())
      : key
        ? this.musicdata.getCuratedAlbums(key)
        : this.musicdata.getTopAlbums(this.selectedGenre());

    this.sourceSub = request.subscribe({
      next: (data) => {
        const list = Array.isArray(data) ? data : (data?.albums?.items ?? []);
        this.items.set(list.slice(0, MAX_ITEMS));
        this.loading.set(false);
        this.ensureDates();
        if (!this.datesLoading()) this.applyRestoredPage();
      },
      error: (err) => {
        console.error('Error loading list:', err);
        this.items.set([]);
        this.error.set('Could not load this list. Please try again.');
        this.loading.set(false);
      }
    });
  }

  // First load of a visit: go back to the page the user was on, once the list it belongs to is ready
  private applyRestoredPage() {
    if (this.restorePage === null) return;
    this.page.set(Math.min(this.restorePage, Math.max(1, this.pages())));
    this.restorePage = null;
  }

  // Chart data has no release dates or track counts. Fetch them as soon as an albums list loads, so the
  // cards are complete and years with no albums can be switched off.
  private ensureDates() {
    const albums = this.items();
    if (this.mode() !== 'albums' || albums.length === 0 || this.datesLoaded() || this.datesLoading()) return;

    this.datesLoading.set(true);
    this.datesSub = this.musicdata.getAlbumDetails(albums.map(a => a.id)).subscribe({
      next: (details: Record<string, AlbumDetails>) => {
        this.items.update(list => list.map(a => {
          const d = details[a.id];
          return d ? { ...a, release_date: d.release_date ?? a.release_date, total_tracks: d.nb_tracks || a.total_tracks, fans: d.fans } : a;
        }));
        this.datesLoaded.set(true);
        this.datesLoading.set(false);
        // A year with no albums in this list (e.g. after switching genre) would only show nothing
        if (this.year() !== 0 && !this.availableYears()!.has(this.year())) this.year.set(0);
        this.applyRestoredPage();
      },
      error: (err) => {
        console.error('Error fetching release dates:', err);
        this.error.set('Could not load release dates, so sorting and filtering by year is unavailable.');
        this.datesLoading.set(false);
        this.applyRestoredPage();
      }
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.sourceSub?.unsubscribe();
    this.datesSub?.unsubscribe();
  }
}
